import { Injectable } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { Observable } from 'rxjs';
import {
    CreateCertificationProvider,
    CreateCertificationProviderFailure,
    CreateCertificationProviderSuccess,
    DeleteCertificationProvider,
    DeleteCertificationProviderFailure,
    DeleteCertificationProviderSuccess,
    FetchCertificatesToReplace,
    FetchCertificationProviders,
    FetchCertificationProvidersFailure,
    FetchCertificationProvidersSearch,
    FetchCertificationProvidersSearchFailure,
    FetchCertificationProvidersSearchSuccess,
    FetchCertificationProvidersSuccess,
    FetchTeamCertificationProvidersSearch,
    FetchTeamMemberCertificationProvidersSearch,
    ReplaceCertificate,
    ReplaceCertificateFailure,
    ReplaceCertificateSuccess,
    UpdateCertificationProvider,
    UpdateCertificationProviderFailure,
    UpdateCertificationProviderStatus,
    UpdateCertificationProviderStatusFailure,
    UpdateCertificationProviderStatusSuccess,
    UpdateCertificationProviderSuccess,
} from '../actions/index';
import { CertificationProviderStatusEnum } from '../enums/certification-provider-status.enum';
import { CertificationProviderTypeEnum } from '../enums/certification-provider-type.enum';
import { CertificationProviderSearchInterface } from '../interfaces/certification-provider-search.interface';
import { CertificationProvider } from '../models/certification-provider';
import { CertificationProviderState } from '../models/certification-provider.state';
import { CertificationProvidersState } from '../models/certification-providers.state';
import { CertificationProviderApiService } from './certification-provider.api-service';

@Injectable()
export class CertificationProviderService {
    @Select(CertificationProviderState.getEntities)
    tag$: Observable<CertificationProvider[]>;

    @Select(CertificationProvidersState.getFilteredEntities)
    filteredCertificationProvider$: Observable<CertificationProvider[]>;

    constructor(private _store: Store, private _actionHelper: NgxsActionHelper, private _certificationProviderApiService: CertificationProviderApiService) {}

    getCertificationProviders(): CertificationProvider[] {
        return this._store.selectSnapshot(CertificationProvidersState.getFilteredEntities);
    }

    async fetchCertificationProvidersSearch(pageSize: number, pageNumber: number, search: string, data: CertificationProviderSearchInterface): Promise<boolean> {
        this._store.dispatch(new FetchCertificationProvidersSearch({ pageSize, pageNumber, search, data }));

        return await this._actionHelper.race(FetchCertificationProvidersSearchSuccess, FetchCertificationProvidersSearchFailure).toPromise();
    }

    async fetchTeamCertificationProvidersSearch(
        pageSize: number,
        pageNumber: number,
        search: string,
        data: CertificationProviderSearchInterface,
        teamId: string,
        includeSubteams: boolean
    ): Promise<boolean> {
        this._store.dispatch(new FetchTeamCertificationProvidersSearch({ pageSize, pageNumber, search, data, teamId, includeSubteams }));

        return await this._actionHelper.race(FetchCertificationProvidersSearchSuccess, FetchCertificationProvidersSearchFailure).toPromise();
    }

    async fetchTeamMemberCertificationProvidersSearch(
        pageSize: number,
        pageNumber: number,
        search: string,
        data: CertificationProviderSearchInterface,
        userId: string
    ): Promise<boolean> {
        this._store.dispatch(new FetchTeamMemberCertificationProvidersSearch({ pageSize, pageNumber, search, data, userId }));

        return await this._actionHelper.race(FetchCertificationProvidersSearchSuccess, FetchCertificationProvidersSearchFailure).toPromise();
    }

    async downloadCertificatesAdmin(pageSize = 10, pageIndex = 1, search = '', payload: CertificationProviderSearchInterface): Promise<void> {
        this._certificationProviderApiService.downloadCertificatesAdmin(pageSize, pageIndex, search, payload).toPromise();
    }

    async downloadCertificatesTeam(
        pageSize = 10,
        pageIndex = 1,
        search = '',
        payload: CertificationProviderSearchInterface,
        teamId: string,
        includeSubteams: boolean = false
    ): Promise<void> {
        this._certificationProviderApiService.downloadCertificatesTeam(pageSize, pageIndex, search, payload, teamId, includeSubteams).toPromise();
    }

    async downloadCertificatesTeamMember(
        pageSize = 10,
        pageIndex = 1,
        search = '',
        payload: CertificationProviderSearchInterface,
        teamId: string,
        userId: string
    ): Promise<void> {
        this._certificationProviderApiService.downloadCertificatesTeamMember(pageSize, pageIndex, search, payload, teamId, userId).toPromise();
    }

    async fetchCertificatesToReplace(pageSize: number, pageNumber: number, certificateId: string): Promise<boolean> {
        this._store.dispatch(new FetchCertificatesToReplace({ pageSize, pageNumber, certificateId }));

        return await this._actionHelper.race(FetchCertificationProvidersSearchSuccess, FetchCertificationProvidersSearchFailure).toPromise();
    }

    async replaceCertificate(certificateId: string, certificationId: string): Promise<boolean> {
        this._store.dispatch(new ReplaceCertificate({ certificateId, certificationId }));

        return await this._actionHelper.race(ReplaceCertificateSuccess, ReplaceCertificateFailure).toPromise();
    }

    async fetchCertificationProviders(pageSize: number, pageNumber: number, search: string, types: number[], statuses: number[]): Promise<boolean> {
        this._store.dispatch(new FetchCertificationProviders({ pageSize, pageNumber, search, types, statuses }));

        return await this._actionHelper.race(FetchCertificationProvidersSuccess, FetchCertificationProvidersFailure).toPromise();
    }

    async createCertificationProvider(name: string, type: CertificationProviderTypeEnum): Promise<boolean> {
        this._store.dispatch(new CreateCertificationProvider({ name, type }));

        return await this._actionHelper.race(CreateCertificationProviderSuccess, CreateCertificationProviderFailure).toPromise();
    }

    async updateCertificationProvider(name: string, id: string): Promise<boolean> {
        this._store.dispatch(new UpdateCertificationProvider({ name, id }));

        return await this._actionHelper.race(UpdateCertificationProviderSuccess, UpdateCertificationProviderFailure).toPromise();
    }

    async deleteCertificationProvider(id: string): Promise<boolean> {
        this._store.dispatch(new DeleteCertificationProvider(id));

        return await this._actionHelper.race(DeleteCertificationProviderSuccess, DeleteCertificationProviderFailure).toPromise();
    }

    async updateCertificationProviderStatus(id: string, status: CertificationProviderStatusEnum): Promise<boolean> {
        this._store.dispatch(new UpdateCertificationProviderStatus({ id, status }));

        return await this._actionHelper.race(UpdateCertificationProviderStatusSuccess, UpdateCertificationProviderStatusFailure).toPromise();
    }
}
